'use client';

import { ComponentRef, forwardRef} from "react";
import { Button, ButtonOwnProps, TypographyProps } from "@mui/material";
import StyledButton from "@/app/features/shared/__to_be_integrated/StyledButton";
import useIsDesktop from "@/hooks/useDesktop";
import { ComponentLinksLink, Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import { omit } from "lodash";
import { PlayForWork, Public } from "@mui/icons-material";
import Link, { LinkProps } from "next/link";
import Typography from "@mui/material/Typography";

interface LinkButtonProps {
    link: Omit<ComponentLinksLink, 'id' | 'type'> & { type?: Enum_Componentlinkslink_Type };
    label?: string;
    labelVariant?: TypographyProps['variant'];
    fullWidth?: boolean;
    disableStyledButtons?: boolean;
    slots?: {
        button?: ButtonOwnProps,
        typography?: TypographyProps
    }
}

const LinkButton = forwardRef<
    ComponentRef<typeof Link>,
    Omit<LinkProps, 'href'> & LinkButtonProps
>(
    ({ link, label, fullWidth = false, disableStyledButtons = false, slots, ...props }, ref) => {

        const isDesktop = useIsDesktop();

        return (
            <Link
                ref={ref}
                href={link.url}
                target={link.type === Enum_Componentlinkslink_Type.External ? '_blank' : '_self'}
                rel={link.type === Enum_Componentlinkslink_Type.External ? 'noopener noreferrer' : undefined}
                passHref
                prefetch
                style={{
                    ...(fullWidth ? { width: '100%' } : {})
                }}
                {...omit(props, 'href')}
            >
                {isDesktop && !disableStyledButtons
                    ? (
                        <StyledButton
                            fullWidth={fullWidth}
                            startIcon={!isDesktop && <Public />}
                            sx={{
                                flexGrow: 1,
                                textAlign:  { xs: 'center', md: 'left' },
                                ...(fullWidth ? { width: '100%' } : {}),
                                ...(isDesktop ? {} : {
                                    position: 'relative',
                                    justifyContent: 'center',
                                    pl: (theme) => `calc(${theme.spacing(4)} + 4px)`,
                                    '& .MuiButton-startIcon': {
                                        position: 'absolute',
                                        left: (theme) => theme.spacing(2),
                                        marginRight: 0,
                                    },
                                }),
                                ...(slots?.button?.sx)
                            }}
                            {...(slots?.button)}
                        >
                            <Typography {...slots?.typography}>{label ?? link.name}</Typography>
                        </StyledButton>
                    ): (
                        <Button
                            fullWidth={fullWidth}
                            variant="outlined"
                            size="large"
                            startIcon={!isDesktop && <PlayForWork />}
                            sx={{
                                textAlign:  { xs: 'center', md: 'left' },
                                ...(isDesktop ? {} : {
                                    position: 'relative',
                                    justifyContent: 'center',
                                    pl: (theme) => `calc(${theme.spacing(4)} + 4px)`,
                                    '& .MuiButton-startIcon': {
                                        position: 'absolute',
                                        left: (theme) => theme.spacing(2),
                                        marginRight: 0,
                                    },
                                })
                            }}
                            {...(slots?.button)}
                        >
                            <Typography {...slots?.typography}>{label ?? link.name}</Typography>
                        </Button>
                    )
                }
            </Link>
        )
    }
);

LinkButton.displayName = "LinkButton";
export default LinkButton;
